/* FundOurKids legislator grader — DOM controller.

   The design drives this UI with its own DCLogic component runtime
   (setState + full re-render). That runtime does not exist in the theme, so
   this file ports the same behaviour as direct DOM updates: read state from
   the form and the toggles, recompute, write the derived values into the
   [data-fok-g] hooks. Behaviour is 1:1 with the design; nothing here decides
   layout or colour beyond the values the design itself computes in JS.

   Scoring maths lives in fok-grader.js (window.FOKGrader) and is unit-tested
   by tests/fok-grader.test.js. This file must never re-implement it.

   Per-row weights come from the block data-attributes (data-weight /
   data-partial), so an edited weight in the theme editor changes the real
   score. FOKGrader's constant arrays remain the fallback when a block omits
   a value. */
(function () {
  'use strict';

  var GREEN = '#2F8F5B', ORANGE = '#E5533D', RED = '#C0392B';

  function init(root) {
    var G = window.FOKGrader;
    if (!G || root.__fokGraderReady) return;
    root.__fokGraderReady = true;

    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-fok-rubitem]'));
    if (!rows.length) return;

    /* Order rows by their declared scoring slot, not by DOM order: the
       category headers are interleaved and the editor may reorder blocks. */
    rows.sort(function (a, b) {
      return (parseInt(a.getAttribute('data-index'), 10) || 0) - (parseInt(b.getAttribute('data-index'), 10) || 0);
    });

    var sel = rows.map(function () { return 'no'; });
    var copyTimer = null;

    /* Legislator picker: values from the client's Open States snapshot. */
    var CHAMBER = { house: 'State House / Assembly', senate: 'State Senate', governor: 'Governor', legislature: 'Unicameral Legislature' };
    var PARTY = { D: 'Democrat', R: 'Republican', I: 'Independent', N: 'Nonpartisan', O: 'Other' };
    var people = [];
    var cache = {};
    var pickedEmail = '';
    var currentAb = '';

    function q(name) { return root.querySelector('[data-fok-g="' + name + '"]'); }
    function text(name, value) { var el = q(name); if (el) el.textContent = value; }

    /* Per-row weights override the module constants. */
    function weightOf(i) {
      var w = parseInt(rows[i].getAttribute('data-weight'), 10);
      return isNaN(w) ? G.WEIGHTS[i] : w;
    }
    function partialOf(i) {
      var p = parseInt(rows[i].getAttribute('data-partial'), 10);
      return isNaN(p) ? G.PARTIAL[i] : p;
    }
    function scores() {
      return sel.map(function (v, i) {
        if (v === 'yes') return weightOf(i);
        if (v === 'partial') return partialOf(i);
        return 0;
      });
    }
    function total() {
      return scores().reduce(function (a, b) { return a + b; }, 0);
    }
    function actions() {
      return scores().filter(function (s) { return s > 0; }).length;
    }

    /* Combined roster, prefetched in the background on load. */
    var combo = q('combo');
    var listEl = q('namelist');
    var nameInput = q('name');
    var loaded = false;
    var activeIdx = -1;
    var shown = [];

    function labelFor(p) {
      var base = (p.t ? p.t + ' ' : '') + p.n;
      return currentAb ? base : base + ' \u00b7 ' + p.s;
    }

    function prefetch() {
      var url = root.getAttribute('data-fok-legislators-base') || '';
      if (!url || !window.fetch) return;
      fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
        if (!d || !d.people) return;
        people = d.people;
        loaded = true;
        if (combo && combo.getAttribute('data-open') === '1') search(nameInput.value);
      })['catch'](function () { /* field stays a plain text input */ });
    }

    function matches(p, terms) {
      if (currentAb && p.s !== currentAb) return false;
      if (!terms.length) return true;
      var hay = ((p.t || '') + ' ' + p.n + ' ' + p.s).toLowerCase();
      for (var i = 0; i < terms.length; i++) {
        if (hay.indexOf(terms[i]) === -1) return false;
      }
      return true;
    }

    function closeList() {
      if (listEl) { listEl.hidden = true; listEl.innerHTML = ''; }
      if (combo) combo.setAttribute('data-open', '0');
      if (nameInput) nameInput.setAttribute('aria-expanded', 'false');
      activeIdx = -1;
      shown = [];
    }

    function note(msg) {
      if (!listEl) return;
      listEl.innerHTML = '<li class="fok-grader__combo-note">' + msg + '</li>';
      listEl.hidden = false;
      if (combo) combo.setAttribute('data-open', '1');
    }

    function search(value) {
      if (!listEl) return;
      if (!loaded) { note('Loading legislators\u2026'); return; }
      var terms = String(value || '').toLowerCase().split(/\s+/).filter(Boolean);
      shown = [];
      for (var i = 0; i < people.length && shown.length < 50; i++) {
        if (matches(people[i], terms)) shown.push(people[i]);
      }
      if (!shown.length) { note('No legislator found \u2014 type any name to use it'); return; }
      var html = '';
      for (var j = 0; j < shown.length; j++) {
        html += '<li class="fok-grader__combo-opt" role="option" id="fok-opt-' + j + '" data-i="' + j + '">' + esc(labelFor(shown[j])) + '</li>';
      }
      if (shown.length === 50) html += '<li class="fok-grader__combo-note">Keep typing to narrow the list</li>';
      listEl.innerHTML = html;
      listEl.hidden = false;
      activeIdx = -1;
      if (combo) combo.setAttribute('data-open', '1');
      if (nameInput) nameInput.setAttribute('aria-expanded', 'true');
    }

    function esc(t) {
      return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function highlight(i) {
      var opts = listEl ? listEl.querySelectorAll('[data-i]') : [];
      for (var k = 0; k < opts.length; k++) opts[k].setAttribute('aria-selected', k === i ? 'true' : 'false');
      activeIdx = i;
      if (i >= 0 && opts[i]) {
        opts[i].scrollIntoView({ block: 'nearest' });
        if (nameInput) nameInput.setAttribute('aria-activedescendant', 'fok-opt-' + i);
      }
    }

    /* Choosing a person fills state, chamber and party and captures the email. */
    function choose(p) {
      if (!p) return;
      pickedEmail = p.e || '';
      if (nameInput) nameInput.value = p.n;
      var stEl = q('state'), ch = q('chamber'), pa = q('party');
      if (stEl && p.s !== currentAb) {
        for (var i = 0; i < stEl.options.length; i++) {
          if (stEl.options[i].getAttribute('data-ab') === p.s) { stEl.selectedIndex = i; break; }
        }
        currentAb = p.s;
      }
      if (ch && CHAMBER[p.c]) ch.value = CHAMBER[p.c];
      if (pa) pa.value = PARTY[p.p] || '';
      closeList();
      render();
    }

    /* A typed name that matches nobody must not keep a previous pick's email. */
    function clearPickIfTyped() {
      if (!nameInput) return;
      for (var i = 0; i < people.length; i++) {
        if (people[i].n === nameInput.value) return;
      }
      if (pickedEmail) { pickedEmail = ''; render(); }
    }

    function resetForNewState() {
      pickedEmail = '';
      var n = q('name'), ch = q('chamber'), pa = q('party');
      if (n) n.value = '';
      if (ch) ch.value = '';
      if (pa) pa.value = '';
      for (var i = 0; i < sel.length; i++) sel[i] = 'no';
      closeList();
      render();
    }

    function fieldValue(name) {
      var el = q(name);
      return el ? el.value : '';
    }

    function shareText() {
      return G.shareText({
        name: fieldValue('name'),
        state: fieldValue('state'),
        sel: sel
      });
    }

    /* --- the design's renderVals(), as direct DOM writes --- */
    function render() {
      var t = total();
      var badge = G.badgeGrade(t);

      text('score', t);
      text('actions', actions());

      var bar = q('bar');
      if (bar) {
        bar.style.width = t + '%';
        /* The design's live bar uses the badge scale's colour, darkened for
           contrast on the light panel exactly as gradeColor does. */
        bar.style.background = t > 0
          ? (badge.color === RED ? '#7A1F12' : badge.color === GREEN ? '#14532D' : '#8A3312')
          : 'rgba(31,29,26,.6)';
      }

      rows.forEach(function (row, i) {
        row.setAttribute('data-on', sel[i] === 'no' ? '0' : '1');
        var tg = row.querySelector('[data-fok-toggle]');
        if (tg) tg.setAttribute('data-sel', sel[i]);
        Array.prototype.forEach.call(row.querySelectorAll('[data-v]'), function (btn) {
          btn.setAttribute('aria-pressed', btn.getAttribute('data-v') === sel[i] ? 'true' : 'false');
        });
      });

      var name = fieldValue('name');
      var st = fieldValue('state');
      var share = shareText();

      /* Links carry live state, so they are rebuilt on every render. */
      var lookup = q('lookup');
      if (lookup) {
        lookup.href = st
          ? 'https://openstates.org/' + abbrevOf(st).toLowerCase() + '/legislators/'
          : 'https://openstates.org/find_your_legislator/';
      }
      var mailto = q('mailto');
      if (mailto) {
        mailto.href = 'mailto:' + encodeURIComponent(pickedEmail) + '?subject=' +
          encodeURIComponent('FundOurKids grade for ' + (name || 'my legislator')) +
          '&body=' + encodeURIComponent(G.emailBody({ sel: sel, name: name, state: st }));
      }
      var tweet = q('tweet');
      if (tweet) {
        tweet.href = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(share.slice(0, 260));
      }

      var card = G.cardGrade(t);
      text('rcName', name || 'My Legislator');
      text('rcGrade', card.letter);
      var rcGrade = q('rcGrade');
      if (rcGrade) rcGrade.style.color = card.color;
      text('rcScore', t + ' / 100 POINTS · ' + actions() + ' OF 10 ACTIONS');
      text('rcState', [fieldValue('party'), st || 'State not selected', fieldValue('chamber')].filter(Boolean).join(' · '));
      text('rcVerdict', card.verdict);
      text('rcDetail', card.detail);
      text('shareText', share);

      var bd = q('rcBreakdown');
      if (bd) {
        bd.textContent = '';
        G.categories(sel).forEach(function (c) {
          var pct = c.max ? c.score / c.max : 0;
          var cell = document.createElement('div');
          cell.className = 'fok-resultcard__cell';
          var val = document.createElement('div');
          val.className = 'fok-resultcard__cell-val';
          val.style.color = pct === 1 ? GREEN : pct >= 0.5 ? ORANGE : RED;
          val.textContent = c.score + '/' + c.max;
          var lab = document.createElement('div');
          lab.className = 'fok-resultcard__cell-label';
          lab.textContent = c.label;
          cell.appendChild(val);
          cell.appendChild(lab);
          bd.appendChild(cell);
        });
      }
    }

    /* Options are server-rendered from snippets/fok-states-data.liquid with data-ab. */
    function abbrevOf(name) {
      var select = q('state');
      if (!select) return '';
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].textContent === name) return select.options[i].getAttribute('data-ab') || '';
      }
      return '';
    }

    /* --- wiring --- */
    rows.forEach(function (row, i) {
      row.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-v]') : null;
        if (!btn || !row.contains(btn)) return;
        sel[i] = btn.getAttribute('data-v');
        render();
      });
    });

    ['name', 'state', 'chamber', 'party'].forEach(function (f) {
      var el = q(f);
      if (el) {
        el.addEventListener('input', render);
        el.addEventListener('change', render);
      }
    });

    function abOf(el) {
      var opt = el.options[el.selectedIndex];
      return opt ? (opt.getAttribute('data-ab') || '') : '';
    }

    if (nameInput && listEl) {
      nameInput.addEventListener('input', function () { search(nameInput.value); clearPickIfTyped(); });
      nameInput.addEventListener('focus', function () { search(nameInput.value); });
      nameInput.addEventListener('keydown', function (e) {
        if (listEl.hidden && (e.key === 'ArrowDown' || e.key === 'Down')) { search(nameInput.value); return; }
        if (e.key === 'ArrowDown' || e.key === 'Down') { e.preventDefault(); highlight(Math.min(activeIdx + 1, shown.length - 1)); }
        else if (e.key === 'ArrowUp' || e.key === 'Up') { e.preventDefault(); highlight(Math.max(activeIdx - 1, 0)); }
        else if (e.key === 'Enter') { if (activeIdx >= 0 && shown[activeIdx]) { e.preventDefault(); choose(shown[activeIdx]); } }
        else if (e.key === 'Escape' || e.key === 'Esc') { closeList(); }
      });
      listEl.addEventListener('mousedown', function (e) {
        var li = e.target.closest ? e.target.closest('[data-i]') : null;
        if (!li) return;
        e.preventDefault();
        choose(shown[parseInt(li.getAttribute('data-i'), 10)]);
      });
      document.addEventListener('click', function (e) {
        if (combo && !combo.contains(e.target)) closeList();
      });
    }

    var stateEl = q('state');
    if (stateEl) {
      currentAb = abOf(stateEl);
      stateEl.addEventListener('change', function () {
        var ab = abOf(stateEl);
        if (ab === currentAb) return;
        currentAb = ab;
        resetForNewState();
      });
    }

    prefetch();

    var generate = q('generate');
    if (generate) {
      generate.addEventListener('click', function () {
        var card = q('result');
        if (!card) return;
        card.hidden = false;
        render();
        window.scrollTo({
          top: card.getBoundingClientRect().top + window.pageYOffset - 40,
          behavior: 'smooth'
        });
        card.focus({ preventScroll: true });
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-fok-g="copy"]'), function (btn) {
      btn.addEventListener('click', function () {
        var label = btn.textContent;
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(shareText()).then(function () {
          btn.textContent = btn.getAttribute('data-copied') || 'Copied ✓';
          clearTimeout(copyTimer);
          copyTimer = setTimeout(function () { btn.textContent = label; }, 2000);
        });
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-fok-g="print"]'), function (btn) {
      btn.addEventListener('click', doPrint);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-fok-g="shareimage"]'), function (btn) {
      btn.addEventListener('click', doShareImage);
    });

    /* --- print (design: _print) --- */
    function doPrint() {
      var t = total();
      var card = G.cardGrade(t);
      var name = fieldValue('name') || 'My Legislator';
      var meta = [fieldValue('party'), fieldValue('state'), fieldValue('chamber')]
        .filter(Boolean).join(' · ') || 'State not specified';

      var rowsHtml = G.LABELS.map(function (l, i) {
        var v = sel[i];
        var mark = v === 'yes' ? 'DONE' : v === 'partial' ? 'PARTIAL' : 'NOT DONE';
        var c = v === 'yes' ? GREEN : v === 'partial' ? ORANGE : '#7c8593';
        var chipBg = v === 'yes' ? '#EEF7F1' : v === 'partial' ? '#FCEFEC' : '#F4F6F9';
        var zebra = i % 2 ? '#FBFCFE' : '#FFFFFF';
        return '<tr style="background:' + zebra + '">' +
          '<td style="padding:9px 12px;font:600 11px Montserrat;color:#1F1D1A">' + esc(l) + '</td>' +
          '<td style="padding:9px 12px;text-align:right;font:700 10px Montserrat;color:' + c +
          ';background:' + chipBg + '">' + mark + '</td></tr>';
      }).join('');

      var catsHtml = G.categories(sel).map(function (c) {
        return '<td style="padding:8px 10px;text-align:center;font:700 12px Montserrat;color:#1F1D1A;border:1px solid #E9EDF3">' +
          c.score + '/' + c.max + '<div style="font:600 8px Montserrat;color:#5a6068;margin-top:3px">' +
          esc(c.label.toUpperCase()) + '</div></td>';
      }).join('');

      var doc = '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(name) +
        ' — FundOurKids Scorecard</title><style>' +
        '@page{size:letter;margin:0}html,body{margin:0;padding:0}body{padding:13mm;' +
        'font-family:Montserrat,sans-serif;color:#1F1D1A;-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
        'table{border-collapse:collapse;width:100%}*{box-sizing:border-box}</style></head><body>' +
        '<h1 style="font:700 22px Montserrat;margin:0 0 4px">' + esc(name) + '</h1>' +
        '<div style="font:600 11px Montserrat;color:#5a6068;margin-bottom:14px">' + esc(meta) + '</div>' +
        '<div style="display:flex;gap:16px;align-items:center;margin-bottom:16px">' +
        '<div style="font:700 54px/1 Montserrat;color:' + card.color + '">' + card.letter + '</div>' +
        '<div><div style="font:700 13px Montserrat">' + esc(card.verdict) + '</div>' +
        '<div style="font:600 11px Montserrat;color:#5a6068;margin-top:4px">' + t +
        ' / 100 points · ' + actions() + ' of 10 actions</div></div></div>' +
        '<div style="font:400 11px/1.5 Montserrat;color:#3F4650;margin-bottom:16px">' + esc(card.detail) + '</div>' +
        '<table style="margin-bottom:16px"><tr>' + catsHtml + '</tr></table>' +
        '<table>' + rowsHtml + '</table>' +
        '<div style="margin-top:18px;font:600 9px Montserrat;color:#8b93a0">FundOurKids.org — 10-Action Legislator Scorecard</div>' +
        '</body></html>';

      var w = window.open('', '_blank');
      /* Popup blocked: the design falls back to printing the current page. */
      if (!w) { window.print(); return; }
      w.document.write(doc);
      w.document.close();
      setTimeout(function () { w.focus(); w.print(); }, 500);
    }

    function esc(s) {
      return String(s).replace(/[&<>"]/g, function (ch) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
      });
    }

    /* --- share image (design: _shareImage) --- */
    function doShareImage() {
      var W = 1080, H = 1080;
      var t = total();
      var card = G.cardGrade(t);
      var name = fieldValue('name') || 'My Legislator';
      var meta = [fieldValue('party'), fieldValue('state'), fieldValue('chamber')]
        .filter(Boolean).join(' · ') || 'State not specified';

      var c = document.createElement('canvas');
      c.width = W; c.height = H;
      var x = c.getContext('2d');

      x.fillStyle = '#1F1D1A';
      x.fillRect(0, 0, W, H);

      x.fillStyle = '#F1F5F9';
      x.font = '700 46px Montserrat, sans-serif';
      x.fillText(name.slice(0, 26), 80, 150);

      x.fillStyle = 'rgba(241,245,249,.6)';
      x.font = '600 24px Montserrat, sans-serif';
      x.fillText(meta.slice(0, 42), 80, 196);

      x.fillStyle = card.color;
      x.font = '700 260px Montserrat, sans-serif';
      x.fillText(card.letter, 80, 490);

      x.fillStyle = '#F1F5F9';
      x.font = '600 40px Montserrat, sans-serif';
      x.fillText(card.verdict, 80, 570);

      x.fillStyle = 'rgba(241,245,249,.6)';
      x.font = '600 26px Montserrat, sans-serif';
      x.fillText(t + ' / 100 POINTS · ' + actions() + ' OF 10 ACTIONS', 80, 620);

      var cy = 700;
      G.categories(sel).forEach(function (cat) {
        var pct = cat.max ? cat.score / cat.max : 0;
        x.fillStyle = 'rgba(241,245,249,.14)';
        x.fillRect(80, cy, W - 160, 40);
        x.fillStyle = pct === 1 ? GREEN : pct >= 0.5 ? ORANGE : RED;
        x.fillRect(80, cy, (W - 160) * pct, 40);
        x.fillStyle = '#F1F5F9';
        x.font = '700 20px Montserrat, sans-serif';
        x.fillText(cat.label + '  ' + cat.score + '/' + cat.max, 96, cy + 28);
        cy += 56;
      });

      x.fillStyle = 'rgba(241,245,249,.6)';
      x.font = '600 24px Montserrat, sans-serif';
      x.fillText('FundOurKids.org', 80, H - 70);

      c.toBlob(function (blob) {
        if (!blob) return;
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'fundourkids-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-scorecard.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
      });
    }

    render();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-fok-grader]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Theme editor re-renders a section into a fresh DOM subtree. */
  document.addEventListener('shopify:section:load', boot);
})();
